/**
 * Merchant for the sign-up form of a visitor who is not signed in.
 *
 * Registration posts to `merchants/<slug>/sellers`, and the slug is normally
 * filled by the seller-authenticated init. A first-time visitor never runs
 * that init, so the form loads the merchant itself from the init endpoint's
 * unauthenticated answer.
 *
 * The request is a plain fetch on purpose: it must never go through a path
 * that reads a 401 as an expired session. Whatever the answer, this module
 * dispatches no logout, shows no message and redirects nowhere. It stores the
 * merchant's id, slug and name only, and only while no merchant is present and
 * no seller session exists.
 */
import store from '../../store/store';
import { updateMerchant } from '../../store/reducers/userSlice';
import { INIT_API } from '../constants/central-api';

let inFlight: Promise<string> | null = null;

function currentMerchantSlug(): string {
  return store.getState().user?.merchant?.merchant_slug ?? '';
}

function hasSellerCredential(): boolean {
  const accessToken = store.getState().auth?.authorization?.accessToken;
  return Boolean(
    accessToken?.sellerToken || accessToken?.currentWhiteLabelSellerToken
  );
}

async function requestMerchant(fetchImpl: typeof fetch): Promise<string> {
  try {
    const response = await fetchImpl(INIT_API, {
      method: 'GET',
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) {
      return '';
    }
    const body: any = await response.json();
    const merchant = body?.data?.merchant;
    const slug: unknown = merchant?.slug;
    if (typeof slug !== 'string' || slug.length === 0) {
      return '';
    }

    // The visitor may have signed in, or the merchant may have arrived another
    // way, while the request was on its way: leave that state alone.
    const existing = currentMerchantSlug();
    if (existing) {
      return existing;
    }
    if (hasSellerCredential()) {
      return '';
    }

    store.dispatch(
      updateMerchant({ id: merchant.id, slug, name: merchant.name })
    );
    return slug;
  } catch (err) {
    return '';
  }
}

/**
 * Make sure the store holds a merchant slug for the sign-up form. Resolves
 * with the slug, or '' when it could not be loaded. Never throws. Concurrent
 * calls share one request; a failed attempt can be repeated.
 */
export function loadSignUpMerchant(
  fetchImpl: typeof fetch = fetch
): Promise<string> {
  const existing = currentMerchantSlug();
  if (existing) {
    return Promise.resolve(existing);
  }
  if (hasSellerCredential()) {
    return Promise.resolve('');
  }
  if (!inFlight) {
    inFlight = requestMerchant(fetchImpl).finally(() => {
      inFlight = null;
    });
  }
  return inFlight;
}
